import { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, X } from "@phosphor-icons/react";
import {
  UploadViewWrapper,
  DropZone,
  DropZoneTitle,
  DropZoneText,
  UploadFileList,
  UploadFileItem,
  UploadRemoveBtn,
  UploadProgress,
  UploadProgressBar,
  PrimaryBtn,
  SecondaryBtn,
  BtnRow,
  MutedText,
} from "../MailIntakeStyled";
import { isPdf, makeEntry, runUploads, tally } from "../uploadQueue";

// A full afternoon of VA downloads is ~1,000 files; listing every name would
// bury the counts that matter.
const NAMES_SHOWN = 50;

const formatSize = (bytes) => {
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

const UploadView = () => {
  const entriesRef = useRef([]);
  const [counts, setCounts] = useState(tally([]));
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [skipped, setSkipped] = useState(0);
  const inputRef = useRef(null);

  const refresh = useCallback(() => setCounts(tally(entriesRef.current)), []);

  useEffect(() => {
    if (!uploading) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);

  const handleFiles = useCallback(
    (fileList) => {
      const all = Array.from(fileList);
      const pdfs = all.filter(isPdf);
      const finished = entriesRef.current.every((e) => e.status === "done");
      if (finished) entriesRef.current = [];
      const seen = new Set(entriesRef.current.map((e) => e.key));
      pdfs.forEach((file) => {
        const entry = makeEntry(file);
        if (!seen.has(entry.key)) {
          seen.add(entry.key);
          entriesRef.current.push(entry);
        }
      });
      setSkipped(all.length - pdfs.length);
      refresh();
    },
    [refresh]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      if (!uploading && e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
    },
    [handleFiles, uploading]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!uploading) setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleBrowseClick = () => {
    if (!uploading) inputRef.current?.click();
  };

  const handleInputChange = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = "";
  };

  const handleRemove = (key) => {
    entriesRef.current = entriesRef.current.filter((entry) => entry.key !== key);
    refresh();
  };

  const handleClear = () => {
    entriesRef.current = [];
    setSkipped(0);
    refresh();
  };

  const handleUpload = async () => {
    if (uploading) return;
    setUploading(true);
    try {
      await runUploads(entriesRef.current, { onChange: refresh });
    } finally {
      setUploading(false);
      refresh();
    }
  };

  const entries = entriesRef.current;
  const failed = entries.filter((e) => e.status === "failed");
  const waiting = entries.filter((e) => e.status === "queued");
  const sent = counts.done + counts.failed;
  const percent = counts.total ? Math.round((sent / counts.total) * 100) : 0;
  const allDone = counts.total > 0 && counts.done === counts.total;
  const notStarted = counts.queued === counts.total && !uploading;
  const totalBytes = entries.reduce((sum, e) => sum + e.file.size, 0);

  return (
    <UploadViewWrapper>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        multiple
        style={{ display: "none" }}
        onChange={handleInputChange}
        data-testid="mail-upload-input"
      />

      <DropZone
        active={dragActive}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleBrowseClick}
      >
        <Paperclip size={40} weight="regular" style={{ marginBottom: 12, opacity: 0.6 }} aria-hidden />
        <DropZoneTitle>{dragActive ? "Drop files here" : "Drag & drop PDF files"}</DropZoneTitle>
        <DropZoneText>
          {uploading
            ? "Uploading — you can add more files when this batch finishes"
            : "or click to browse — PDF files only. Each file goes straight into the sorting queue."}
        </DropZoneText>
      </DropZone>

      {skipped > 0 && (
        <MutedText style={{ marginTop: 12 }}>
          {plural(skipped, "file")} skipped because {skipped === 1 ? "it is" : "they are"} not a PDF.
        </MutedText>
      )}

      {counts.total > 0 && (
        <UploadFileList>
          {notStarted && (
            <MutedText>
              {plural(counts.total, "file")} ready ({formatSize(totalBytes)})
            </MutedText>
          )}

          {!notStarted && (
            <div role="status" aria-live="polite">
              <div style={{ fontWeight: 500 }}>
                {allDone
                  ? `All ${plural(counts.total, "file")} received. They are in the sorting queue now.`
                  : `${counts.done.toLocaleString()} of ${plural(counts.total, "file")} received` +
                    (counts.failed ? `, ${counts.failed.toLocaleString()} failed` : "")}
              </div>
              {uploading && (
                <MutedText>Keep this tab open until it finishes.</MutedText>
              )}
              <UploadProgress>
                <UploadProgressBar percent={percent} />
              </UploadProgress>
            </div>
          )}

          {failed.length > 0 && !uploading && (
            <>
              <div style={{ fontWeight: 500, marginTop: 8 }}>
                {failed.length === 1 ? "This file did not arrive:" : `These ${failed.length.toLocaleString()} files did not arrive:`}
              </div>
              {failed.slice(0, NAMES_SHOWN).map((entry) => (
                <UploadFileItem key={entry.key}>
                  <div>
                    <span style={{ fontWeight: 500 }}>{entry.file.name}</span>
                    <MutedText style={{ marginLeft: 8 }}>{entry.error}</MutedText>
                  </div>
                </UploadFileItem>
              ))}
              {failed.length > NAMES_SHOWN && (
                <MutedText>…and {plural(failed.length - NAMES_SHOWN, "more file")}</MutedText>
              )}
            </>
          )}

          {notStarted &&
            waiting.slice(0, NAMES_SHOWN).map((entry) => (
              <UploadFileItem key={entry.key}>
                <div>
                  <span style={{ fontWeight: 500 }}>{entry.file.name}</span>
                  <MutedText style={{ marginLeft: 8 }}>{formatSize(entry.file.size)}</MutedText>
                </div>
                <UploadRemoveBtn aria-label={`Remove ${entry.file.name}`} onClick={() => handleRemove(entry.key)}>
                  <X size={14} weight="bold" />
                </UploadRemoveBtn>
              </UploadFileItem>
            ))}
          {notStarted && waiting.length > NAMES_SHOWN && (
            <MutedText>…and {plural(waiting.length - NAMES_SHOWN, "more file")}</MutedText>
          )}

          {!uploading && !allDone && (
            <BtnRow style={{ marginTop: 8 }}>
              <PrimaryBtn onClick={handleUpload}>
                {counts.queued > 0
                  ? `Upload ${plural(counts.queued + counts.failed, "file")}`
                  : `Retry ${plural(failed.length, "failed file")}`}
              </PrimaryBtn>
              <SecondaryBtn onClick={handleClear}>Clear</SecondaryBtn>
            </BtnRow>
          )}
        </UploadFileList>
      )}
    </UploadViewWrapper>
  );
};

export default UploadView;
